import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../common/Button';
import AdminHeader from './AdminHeader';
import Modal from '../common/Modal';
import { getHolidaysForYears } from '../../utils/holidays';

const GymSettings = () => {
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    reservation_start_hour: 6,
    reservation_end_hour: 22,
    available_durations: [30, 60],
    default_duration: 30,
    operating_days: [1, 2, 3, 4, 5, 6, 7], // Monday to Sunday
    max_advance_booking_days: 7,
    min_advance_booking_minutes: 0,
    cancellation_deadline_minutes: 60,
    auto_generate_slots: true,
    generation_window_days: 7
  });

  // Blocked dates state
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newBlockedReason, setNewBlockedReason] = useState('');
  const [isAddingBlockedDate, setIsAddingBlockedDate] = useState(false);

  // Holiday modal state
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [selectedHolidays, setSelectedHolidays] = useState({});
  const [selectedYears, setSelectedYears] = useState([]);
  const [isAddingHolidays, setIsAddingHolidays] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // For gym_manager, use their gym_id; for others, this would need gym selection
      const gymId = user.gymId || 1; // Fallback for testing
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/gym-settings/${gymId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load gym settings');
      }

      const data = await response.json();
      setSettings(data);
      setFormData({
        reservation_start_hour: data.reservation_start_hour,
        reservation_end_hour: data.reservation_end_hour,
        available_durations: data.available_durations,
        default_duration: data.default_duration,
        operating_days: data.operating_days,
        max_advance_booking_days: data.max_advance_booking_days,
        min_advance_booking_minutes: data.min_advance_booking_minutes,
        cancellation_deadline_minutes: data.cancellation_deadline_minutes,
        auto_generate_slots: data.auto_generate_slots,
        generation_window_days: data.generation_window_days
      });
      setBlockedDates(data.blocked_dates || []);
    } catch (error) {
      console.error('Error loading settings:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      const gymId = user.gymId || 1;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/gym-settings/${gymId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.details || errorData.error || `Failed to save settings (${response.status})`;
        throw new Error(errorMessage);
      }

      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setSuccessMessage('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDurationToggle = (duration) => {
    setFormData(prev => {
      // Set only the selected duration (exclusive selection)
      return {
        ...prev,
        available_durations: [duration],
        default_duration: duration
      };
    });
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const currentDays = prev.operating_days;
      const isSelected = currentDays.includes(day);
      
      if (isSelected) {
        return {
          ...prev,
          operating_days: currentDays.filter(d => d !== day)
        };
      } else {
        return {
          ...prev,
          operating_days: [...currentDays, day].sort()
        };
      }
    });
  };

  const handleAddBlockedDate = async () => {
    if (!newBlockedDate || !newBlockedReason.trim()) {
      setError('Please provide both date and reason');
      return;
    }

    try {
      setIsAddingBlockedDate(true);
      setError(null);

      const gymId = user.gymId || 1;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/blocked-dates/gym/${gymId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          blocked_date: newBlockedDate,
          reason: newBlockedReason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add blocked date');
      }

      const newDate = await response.json();
      setBlockedDates(prev => [...prev, newDate].sort((a, b) =>
        new Date(a.blocked_date) - new Date(b.blocked_date)
      ));
      setNewBlockedDate('');
      setNewBlockedReason('');
      setSuccessMessage('Blocked date added successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error adding blocked date:', error);
      setError(error.message);
    } finally {
      setIsAddingBlockedDate(false);
    }
  };

  const handleRemoveBlockedDate = async (blockedDateId) => {
    try {
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/blocked-dates/${blockedDateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove blocked date');
      }

      setBlockedDates(prev => prev.filter(d => d.id !== blockedDateId));
      setSuccessMessage('Blocked date removed successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error removing blocked date:', error);
      setError(error.message);
    }
  };

  const handleOpenHolidayModal = () => {
    const currentYear = new Date().getFullYear();
    // Default to current year and next year
    setSelectedYears([currentYear, currentYear + 1]);
    setSelectedHolidays({});
    setShowHolidayModal(true);
  };

  const handleToggleHoliday = (holidayName) => {
    setSelectedHolidays(prev => ({
      ...prev,
      [holidayName]: !prev[holidayName]
    }));
  };

  const handleToggleYear = (year) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        return prev.filter(y => y !== year);
      } else {
        return [...prev, year].sort();
      }
    });
  };

  const handleAddHolidays = async () => {
    try {
      setIsAddingHolidays(true);
      setError(null);

      const gymId = user.gymId || 1;
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      // Get all holidays for selected years
      const startYear = Math.min(...selectedYears);
      const endYear = Math.max(...selectedYears);
      const holidaysByYear = getHolidaysForYears(startYear, endYear);

      // Build list of holidays to add
      const holidaysToAdd = [];
      selectedYears.forEach(year => {
        const yearHolidays = holidaysByYear[year];
        yearHolidays.forEach(holiday => {
          if (selectedHolidays[holiday.name]) {
            const holidayDate = new Date(holiday.date + 'T00:00:00');
            // Only add if date is in the future or today
            if (holidayDate >= currentDate) {
              holidaysToAdd.push({
                blocked_date: holiday.date,
                reason: holiday.reason
              });
            }
          }
        });
      });

      if (holidaysToAdd.length === 0) {
        setError('No holidays selected or all selected holidays are in the past');
        setIsAddingHolidays(false);
        return;
      }

      // Add all holidays
      const addedHolidays = [];
      for (const holiday of holidaysToAdd) {
        // Check if already exists
        const exists = blockedDates.some(bd => bd.blocked_date === holiday.blocked_date);
        if (exists) {
          continue; // Skip if already blocked
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/blocked-dates/gym/${gymId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(holiday)
        });

        if (response.ok) {
          const newDate = await response.json();
          addedHolidays.push(newDate);
        }
      }

      if (addedHolidays.length > 0) {
        setBlockedDates(prev => [...prev, ...addedHolidays].sort((a, b) =>
          new Date(a.blocked_date) - new Date(b.blocked_date)
        ));
        setSuccessMessage(`Added ${addedHolidays.length} holiday(s) successfully!`);
        setTimeout(() => setSuccessMessage(null), 3000);
        setShowHolidayModal(false);
      } else {
        setError('All selected holidays are already blocked');
      }
    } catch (error) {
      console.error('Error adding holidays:', error);
      setError(error.message);
    } finally {
      setIsAddingHolidays(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatHour = (hour) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${ampm}`;
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const durationOptions = [15, 30, 45, 60, 90, 120];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading gym settings..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminHeader
          title="Gym Settings"
          description="Configure reservation hours, policies, and time slot generation for your gym"
        >
          {settings && (
            <p className="mt-2 text-sm text-blue-600">
              Managing: {settings.gym_name}
            </p>
          )}
        </AdminHeader>


        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-6 space-y-8">
            
            {/* Reservation Hours */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reservation Hours</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Hour
                  </label>
                  <select
                    value={formData.reservation_start_hour}
                    onChange={(e) => handleInputChange('reservation_start_hour', parseInt(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{formatHour(i)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Hour
                  </label>
                  <select
                    value={formData.reservation_end_hour}
                    onChange={(e) => handleInputChange('reservation_end_hour', parseInt(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{formatHour(i)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Session Durations */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Session Duration</h3>
              <div>
                <p className="text-sm text-gray-600 mb-3">Select session length (minutes):</p>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {durationOptions.map(duration => (
                    <button
                      key={duration}
                      onClick={() => handleDurationToggle(duration)}
                      className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                        formData.available_durations.includes(duration)
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
                      }`}
                    >
                      {duration}m
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Operating Days */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Operating Days</h3>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                {dayNames.map((day, index) => (
                  <button
                    key={index}
                    onClick={() => handleDayToggle(index)}
                    className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                      formData.operating_days.includes(index)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
                    }`}
                  >
                    {day.substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            {/* Booking Policies */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Booking Policies</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Advance Booking (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.max_advance_booking_days}
                    onChange={(e) => handleInputChange('max_advance_booking_days', parseInt(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Advance Booking (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1440"
                    value={formData.min_advance_booking_minutes}
                    onChange={(e) => handleInputChange('min_advance_booking_minutes', parseInt(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cancellation Deadline (minutes before start)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1440"
                    value={formData.cancellation_deadline_minutes}
                    onChange={(e) => handleInputChange('cancellation_deadline_minutes', parseInt(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Auto-generation Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Time Slot Generation</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="auto_generate"
                    checked={formData.auto_generate_slots}
                    onChange={(e) => handleInputChange('auto_generate_slots', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="auto_generate" className="ml-2 block text-sm text-gray-900">
                    Automatically generate time slots
                  </label>
                </div>
                {formData.auto_generate_slots && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Generation Window (days ahead)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={formData.generation_window_days}
                      onChange={(e) => handleInputChange('generation_window_days', parseInt(e.target.value))}
                      className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Time slots will be automatically generated this many days in advance
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Blocked Dates */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Blocked Dates</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Block specific dates when your gym is closed for holidays, special events, or maintenance.
                  </p>
                </div>
                <Button
                  onClick={handleOpenHolidayModal}
                  variant="outline"
                  size="small"
                >
                  Add US Holidays
                </Button>
              </div>

              {/* Add New Blocked Date */}
              <div className="mb-4 p-4 bg-gray-50 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={newBlockedDate}
                      onChange={(e) => setNewBlockedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason
                    </label>
                    <input
                      type="text"
                      value={newBlockedReason}
                      onChange={(e) => setNewBlockedReason(e.target.value)}
                      placeholder="e.g., Christmas Day"
                      maxLength={255}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleAddBlockedDate}
                      loading={isAddingBlockedDate}
                      disabled={isAddingBlockedDate || !newBlockedDate || !newBlockedReason.trim()}
                      className="w-full"
                    >
                      Add Blocked Date
                    </Button>
                  </div>
                </div>
              </div>

              {/* List of Blocked Dates */}
              {blockedDates.length > 0 ? (
                <div className="space-y-2">
                  {blockedDates.map((blockedDate) => (
                    <div
                      key={blockedDate.id}
                      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDate(blockedDate.blocked_date)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {blockedDate.reason}
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveBlockedDate(blockedDate.id)}
                        className="ml-3 text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  No blocked dates configured. Add dates above to block reservations on specific days.
                </p>
              )}
            </div>

            {/* Save Button */}
            <div className="border-t pt-6">
              
              {/* Error and Success Messages */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Unable to Save Settings</h3>
                      <p className="mt-2 text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">{successMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button
                  onClick={loadSettings}
                  variant="outline"
                  disabled={isSaving}
                >
                  Reset
                </Button>
                <Button
                  onClick={handleSave}
                  loading={isSaving}
                  disabled={isSaving}
                >
                  Save Settings
                </Button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Holiday Selection Modal */}
      <Modal
        isOpen={showHolidayModal}
        onClose={() => setShowHolidayModal(false)}
        title="Add US Federal Holidays"
        footer={
          <div className="space-x-3">
            <Button
              onClick={() => setShowHolidayModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddHolidays}
              loading={isAddingHolidays}
              disabled={isAddingHolidays || selectedYears.length === 0 || Object.values(selectedHolidays).every(v => !v)}
            >
              Add Selected Holidays
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Year Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Select Years
            </label>
            <div className="flex flex-wrap gap-2">
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() + i;
                return (
                  <button
                    key={year}
                    onClick={() => handleToggleYear(year)}
                    className={`px-4 py-2 text-sm border rounded-md transition-colors ${
                      selectedYears.includes(year)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
                    }`}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Select which years to block holidays for
            </p>
          </div>

          {/* Holiday Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Select Holidays
            </label>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(() => {
                // Get holidays for first selected year to show options
                const sampleYear = selectedYears[0] || new Date().getFullYear();
                const sampleHolidays = getHolidaysForYears(sampleYear, sampleYear)[sampleYear];

                return sampleHolidays.map((holiday) => (
                  <div
                    key={holiday.name}
                    className="flex items-center p-3 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleToggleHoliday(holiday.name)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedHolidays[holiday.name] || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleHoliday(holiday.name);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-3 block text-sm text-gray-900 cursor-pointer flex-1">
                      {holiday.name}
                    </label>
                    <span className="text-xs text-gray-500">
                      {new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ));
              })()}
            </div>
            {selectedYears.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                Selected holidays will be blocked for: {selectedYears.join(', ')}
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GymSettings;